import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { EmptyCustomers } from "@/components/ui/empty-state-presets";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { getLoginUrl } from "@/const";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Users,
  DollarSign,
  TrendingUp,
  Building2,
} from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";
import { toast } from "@/lib/toast";
import { useIsMobile } from "@/hooks/useMobile";
import DashboardLayout from "@/components/DashboardLayout";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";

type CustomerFormData = {
  id?: number;
  companyId: number;
  name: string;
  legalName?: string;
  customerNumber?: string;
  vatNumber?: string;
  country?: string;
  customerType?: "individual" | "company" | "government";
  industry?: string;
  segment?: "enterprise" | "smb" | "consumer";
  email?: string;
  phone?: string;
  website?: string;
  address?: {
    street?: string;
    city?: string;
    postalCode?: string;
    canton?: string;
  };
  vatExempt?: boolean;
  vatRateOverride?: number;
  creditLimit?: number;
  paymentTerms?: number;
  riskScore?: number;
  notes?: string;
  tags?: string[];
};

export default function Customers() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { selectedCompanyId } = useCompany();
  const [, setLocation] = useLocation();
  const isMobile = useIsMobile();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [customerTypeFilter, setCustomerTypeFilter] = useState<string>("all");
  const [segmentFilter, setSegmentFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const [formData, setFormData] = useState<CustomerFormData>({
    companyId: selectedCompanyId || 0,
    name: "",
    legalName: "",
    customerNumber: "",
    vatNumber: "",
    country: "CH",
    customerType: undefined,
    industry: "",
    segment: undefined,
    email: "",
    phone: "",
    website: "",
    address: {
      street: "",
      city: "",
      postalCode: "",
      canton: "",
    },
    vatExempt: false,
    vatRateOverride: undefined,
    creditLimit: undefined,
    paymentTerms: undefined,
    riskScore: undefined,
    notes: "",
    tags: [],
  });

  // Fetch customers
  const { data: customersData, refetch } =
    trpc.customers.listCustomers.useQuery(
      {
        companyId: selectedCompanyId || 0,
        search: searchQuery || undefined,
        customerType:
          customerTypeFilter !== "all"
            ? (customerTypeFilter as any)
            : undefined,
        segment: segmentFilter !== "all" ? (segmentFilter as any) : undefined,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      },
      {
        enabled: isAuthenticated && !!selectedCompanyId,
      }
    );
  const customers = customersData?.items ?? [];
  const customerTotal = customersData?.total ?? 0;
  const totalPages = Math.ceil(customerTotal / pageSize);

  // Fetch customer stats
  const { data: stats } = trpc.customers.getCustomerStats.useQuery(
    {
      companyId: selectedCompanyId || 0,
    },
    {
      enabled: isAuthenticated && !!selectedCompanyId,
    }
  );

  // Mutations
  const createMutation = trpc.customers.createCustomer.useMutation({
    onSuccess: () => {
      refetch();
      setIsCreateDialogOpen(false);
      toast.created("Customer", {});
    },
    onError: error => {
      toast.error(error.message || "Failed to create customer");
    },
  });

  const updateMutation = trpc.customers.updateCustomer.useMutation({
    onSuccess: () => {
      refetch();
      setIsEditDialogOpen(false);
      toast.success("Customer updated");
    },
    onError: error => {
      toast.error(error.message || "Failed to update customer");
    },
  });

  const deleteMutation = trpc.customers.deleteCustomer.useMutation({
    onSuccess: () => {
      const deletedCustomer = selectedCustomer;
      toast.deleted("Customer", {
        onUndo: () => {
          if (deletedCustomer && selectedCompanyId) {
            // Restore by creating with same data
            createMutation.mutate({
              companyId: selectedCompanyId,
              name: deletedCustomer.name,
              email: deletedCustomer.email || undefined,
              phone: deletedCustomer.phone || undefined,
              country: deletedCustomer.country || "CH",
            });
            toast.success("Customer restored");
          }
        },
      });
      refetch();
      setIsDeleteDialogOpen(false);
    },
    onError: error => {
      toast.error(error.message || "Failed to delete customer");
    },
  });

  // Redirect to login if not authenticated
  if (!authLoading && !isAuthenticated) {
    setLocation(getLoginUrl());
    return null;
  }

  // Loading state
  if (authLoading || !user) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-lg">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  // No company selected
  if (!selectedCompanyId) {
    return (
      <DashboardLayout>
        <div className="container mx-auto py-8">
          <Card>
            <CardHeader>
              <CardTitle>Select a Company</CardTitle>
              <CardDescription>
                Please select a company from the sidebar to view customers.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  const handleCreate = () => {
    setFormData({
      companyId: selectedCompanyId,
      name: "",
      legalName: "",
      customerNumber: "",
      vatNumber: "",
      country: "CH",
      customerType: undefined,
      industry: "",
      segment: undefined,
      email: "",
      phone: "",
      website: "",
      address: {
        street: "",
        city: "",
        postalCode: "",
        canton: "",
      },
      vatExempt: false,
      vatRateOverride: undefined,
      creditLimit: undefined,
      paymentTerms: undefined,
      riskScore: undefined,
      notes: "",
      tags: [],
    });
    setIsCreateDialogOpen(true);
  };

  const handleEdit = (customer: any) => {
    setSelectedCustomer(customer);
    setFormData({
      id: customer.id,
      companyId: customer.companyId,
      name: customer.name || "",
      legalName: customer.legalName || "",
      customerNumber: customer.customerNumber || "",
      vatNumber: customer.vatNumber || "",
      country: customer.country || "CH",
      customerType: customer.customerType,
      industry: customer.industry || "",
      segment: customer.segment,
      email: customer.email || "",
      phone: customer.phone || "",
      website: customer.website || "",
      address: {
        street: customer.street || "",
        city: customer.city || "",
        postalCode: customer.postalCode || "",
        canton: customer.canton || "",
      },
      vatExempt: customer.vatExempt || false,
      vatRateOverride: customer.vatRateOverride,
      creditLimit: customer.creditLimit,
      paymentTerms: customer.paymentTerms,
      riskScore: customer.riskScore,
      notes: customer.notes || "",
      tags: (customer.tags as string[]) || [],
    });
    setIsEditDialogOpen(true);
  };

  const handleDelete = (customer: any) => {
    setSelectedCustomer(customer);
    setIsDeleteDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isEditDialogOpen) {
      updateMutation.mutate({
        id: formData.id!,
        companyId: formData.companyId,
        name: formData.name,
        legalName: formData.legalName,
        customerNumber: formData.customerNumber,
        vatNumber: formData.vatNumber,
        country: formData.country,
        customerType: formData.customerType,
        industry: formData.industry,
        segment: formData.segment,
        email: formData.email,
        phone: formData.phone,
        website: formData.website,
        address: formData.address,
        vatExempt: formData.vatExempt,
        vatRateOverride: formData.vatRateOverride,
        creditLimit: formData.creditLimit,
        paymentTerms: formData.paymentTerms,
        riskScore: formData.riskScore,
        notes: formData.notes,
        tags: formData.tags,
      });
    } else {
      createMutation.mutate({
        companyId: formData.companyId,
        name: formData.name,
        legalName: formData.legalName,
        customerNumber: formData.customerNumber,
        vatNumber: formData.vatNumber,
        country: formData.country,
        customerType: formData.customerType,
        industry: formData.industry,
        segment: formData.segment,
        email: formData.email,
        phone: formData.phone,
        website: formData.website,
        address: formData.address,
        vatExempt: formData.vatExempt,
        vatRateOverride: formData.vatRateOverride,
        creditLimit: formData.creditLimit,
        paymentTerms: formData.paymentTerms,
        riskScore: formData.riskScore,
        notes: formData.notes,
        tags: formData.tags,
      });
    }
  };

  const confirmDelete = () => {
    if (selectedCustomer) {
      deleteMutation.mutate({
        id: selectedCustomer.id,
        companyId: selectedCustomer.companyId,
      });
    }
  };

  const formatCurrency = (amount: number | null) => {
    if (!amount) return "CHF 0.00";
    return `CHF ${(amount / 100).toLocaleString("de-CH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <DashboardLayout>
      <div className="container mx-auto py-8 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Customers</h1>
            <p className="text-muted-foreground">
              Manage your clients and track revenue
            </p>
          </div>
          <Button onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add Customer
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Customers
                </CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalCustomers}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Revenue
                </CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(stats.totalRevenue || 0)}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Enterprise
                </CardTitle>
                <Building2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats.enterpriseCustomers}
                </div>
                <p className="text-xs text-muted-foreground">
                  SMB: {stats.smbCustomers}, Consumer: {stats.consumerCustomers}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Avg Revenue/Customer
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(stats.avgRevenuePerCustomer || 0)}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search customers..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select
                value={customerTypeFilter}
                onValueChange={setCustomerTypeFilter}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="company">Company</SelectItem>
                  <SelectItem value="government">Government</SelectItem>
                </SelectContent>
              </Select>
              <Select value={segmentFilter} onValueChange={setSegmentFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Segments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Segments</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                  <SelectItem value="smb">SMB</SelectItem>
                  <SelectItem value="consumer">Consumer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Customers Table */}
        <Card>
          <CardHeader>
            <CardTitle>Customer List</CardTitle>
            <CardDescription>
              {customerTotal} customer{customerTotal !== 1 ? "s" : ""} found
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Segment</TableHead>
                  <TableHead className="text-right">Transactions</TableHead>
                  <TableHead className="text-right">Total Revenue</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="p-0">
                      <EmptyCustomers
                        size="sm"
                        onPrimaryAction={() => setIsCreateDialogOpen(true)}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  customers.map(customer => (
                    <TableRow key={customer.id}>
                      <TableCell className="font-medium">
                        <div>
                          <div>{customer.name}</div>
                          {customer.vatExempt && (
                            <Badge variant="outline" className="mt-1">
                              VAT Exempt
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {customer.customerType ? (
                          <Badge variant="secondary">
                            {customer.customerType}
                          </Badge>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {customer.segment ? (
                          <Badge variant="secondary">{customer.segment}</Badge>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {customer.transactionCount || 0}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(customer.totalRevenue || 0)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(customer)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(customer)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        className={
                          currentPage === 1
                            ? "pointer-events-none opacity-50"
                            : "cursor-pointer"
                        }
                      />
                    </PaginationItem>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <PaginationItem key={pageNum}>
                          <PaginationLink
                            isActive={currentPage === pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className="cursor-pointer"
                          >
                            {pageNum}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    })}
                    {totalPages > 5 && currentPage < totalPages - 2 && (
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                    )}
                    <PaginationItem>
                      <PaginationNext
                        onClick={() =>
                          setCurrentPage(p => Math.min(totalPages, p + 1))
                        }
                        className={
                          currentPage === totalPages
                            ? "pointer-events-none opacity-50"
                            : "cursor-pointer"
                        }
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
                <p className="text-sm text-muted-foreground text-center mt-2">
                  Showing {(currentPage - 1) * pageSize + 1}-
                  {Math.min(currentPage * pageSize, customerTotal)} of{" "}
                  {customerTotal} customers
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog
        open={isCreateDialogOpen || isEditDialogOpen}
        onOpenChange={open => {
          if (!open) {
            setIsCreateDialogOpen(false);
            setIsEditDialogOpen(false);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isEditDialogOpen ? "Edit Customer" : "Create Customer"}
            </DialogTitle>
            <DialogDescription>
              {isEditDialogOpen
                ? "Update customer information"
                : "Add a new customer to your system"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="font-medium">Basic Information</h3>

                <div className="grid gap-2">
                  <Label htmlFor="name">
                    Customer Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={e =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="legalName">Legal Name</Label>
                  <Input
                    id="legalName"
                    value={formData.legalName}
                    onChange={e =>
                      setFormData({ ...formData, legalName: e.target.value })
                    }
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="customerNumber">Customer Number</Label>
                    <Input
                      id="customerNumber"
                      value={formData.customerNumber}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          customerNumber: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="vatNumber">VAT Number</Label>
                    <Input
                      id="vatNumber"
                      value={formData.vatNumber}
                      onChange={e =>
                        setFormData({ ...formData, vatNumber: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="customerType">Type</Label>
                    <Select
                      value={formData.customerType}
                      onValueChange={(value: any) =>
                        setFormData({ ...formData, customerType: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="individual">Individual</SelectItem>
                        <SelectItem value="company">Company</SelectItem>
                        <SelectItem value="government">Government</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="segment">Segment</Label>
                    <Select
                      value={formData.segment}
                      onValueChange={(value: any) =>
                        setFormData({ ...formData, segment: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select segment" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                        <SelectItem value="smb">SMB</SelectItem>
                        <SelectItem value="consumer">Consumer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="industry">Industry</Label>
                    <Input
                      id="industry"
                      value={formData.industry}
                      onChange={e =>
                        setFormData({ ...formData, industry: e.target.value })
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium">Contact Information</h3>

                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={e =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={e =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      value={formData.website}
                      onChange={e =>
                        setFormData({ ...formData, website: e.target.value })
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium">Address</h3>

                <div className="grid gap-2">
                  <Label htmlFor="street">Street</Label>
                  <Input
                    id="street"
                    value={formData.address?.street}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        address: {
                          ...formData.address,
                          street: e.target.value,
                        },
                      })
                    }
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="postalCode">Postal Code</Label>
                    <Input
                      id="postalCode"
                      value={formData.address?.postalCode}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          address: {
                            ...formData.address,
                            postalCode: e.target.value,
                          },
                        })
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={formData.address?.city}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          address: {
                            ...formData.address,
                            city: e.target.value,
                          },
                        })
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="canton">Canton</Label>
                    <Input
                      id="canton"
                      value={formData.address?.canton}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          address: {
                            ...formData.address,
                            canton: e.target.value,
                          },
                        })
                      }
                      placeholder="ZH"
                      maxLength={2}
                    />
                  </div>
                </div>
              </div>

              {/* VAT & Financial Settings */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium">VAT & Financial Settings</h3>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="vatExempt"
                    checked={formData.vatExempt}
                    onChange={e =>
                      setFormData({ ...formData, vatExempt: e.target.checked })
                    }
                    className="h-4 w-4"
                  />
                  <Label htmlFor="vatExempt">VAT Exempt</Label>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="creditLimit">Credit Limit (CHF)</Label>
                    <Input
                      id="creditLimit"
                      type="number"
                      value={formData.creditLimit || ""}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          creditLimit: e.target.value
                            ? Number(e.target.value)
                            : undefined,
                        })
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="paymentTerms">Payment Terms (days)</Label>
                    <Input
                      id="paymentTerms"
                      type="number"
                      value={formData.paymentTerms || ""}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          paymentTerms: e.target.value
                            ? Number(e.target.value)
                            : undefined,
                        })
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="riskScore">Risk Score (0-100)</Label>
                    <Input
                      id="riskScore"
                      type="number"
                      min="0"
                      max="100"
                      value={formData.riskScore || ""}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          riskScore: e.target.value
                            ? Number(e.target.value)
                            : undefined,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-4 pt-4 border-t">
                <div className="grid gap-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={e =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    rows={3}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  setIsEditDialogOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending
                  ? "Saving..."
                  : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Customer</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this customer? This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
